import Moment from "moment";
import React, { useState } from "react";
import { connect, useDispatch } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { getAdminDetails, logOut, saveToken } from "./actions/auth";
import * as routes from "./constants/routes";
import DashboardScreen from "./containers/dashboardScreen";
import NotFound from "./containers/NotFoundContainer";
import { Toaster, ToastProvider } from "./ui/composed/toast";
import { TooltipProvider } from "./ui/primitives/tooltip";
import {
  appCheckSetup,
  onTokenIdChanged,
  userChangeListener,
} from "./utils/firebase";
import DashboardScreenV2 from "./v2/containers/dashboardScreen";
import "@/styles/globals.css";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { FlagProvider } from "@unleash/proxy-client-react";
import { cityList, clearSavedCity } from "./actions/cities";
import { InfoContainer } from "./containers/info/InfoContainer";
import { useIsAdminAsset } from "./hooks/user-hooks";
import ForgotPasswordScreen from "./v2/containers/forgotPasswordScreen";
import LoginScreen from "./v2/containers/loginScreen";
import PartnerView from "./v2/containers/partnerView";
import { useAuthStore } from "./v2/stores/authStore";

const flagConfig = {
  url: `${process.env.REACT_APP_UNLEASH_URL || "https://unleash.dev.lisasolutions.com/"}frontend`,
  clientKey: process.env.REACT_APP_UNLEASH_KEY || "default-client-key",
  refreshInterval: 15,
  appName: "lisa-dashboard",
};

function CustomRedirect({ children, redirectTo, showChildrenWhen }) {
  return showChildrenWhen ? children : <Navigate to={redirectTo} />;
}

function App(props) {
  const [showApp, setShowApp] = useState(false);
  const dispatch = useDispatch();
  const isAdminAsset = useIsAdminAsset();

  React.useEffect(() => {
    appCheckSetup();

    userChangeListener(async (user) => {
      if (user) {
        await dispatch(getAdminDetails());
        await dispatch(cityList());
      } else {
        // Persisted session state can outlive the Firebase session; Firebase is the authority.
        dispatch(logOut());
        dispatch(clearSavedCity());
        useAuthStore.getState().clearAuth();
      }
      setShowApp(true);
    });

    if (props.user) {
      onTokenIdChanged(updateToken);
    }

    // Hide Zendesk widget launcher - triggered from navbar help button instead
    if (window.zE) {
      // Hide the launcher button
      window.zE("webWidget", "hide");
      // Configure position for when it opens
      window.zE("webWidget", "updateSettings", {
        webWidget: {
          position: { horizontal: "right", vertical: "bottom" },
          offset: { horizontal: "16px", vertical: "16px" },
          zIndex: 40,
        },
      });
    }
    // return logoutFromFirebase
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateToken = (user) => {
    if (user) {
      user.getIdToken().then(props.saveToken);
    }
  };

  Moment.updateLocale("en", {
    week: {
      dow: 1,
    },
  });

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <FlagProvider config={flagConfig}>
        <TooltipProvider delayDuration={200}>
          {showApp ? (
            <BrowserRouter>
              <ToastProvider>
                <Toaster />
                <Routes>
                  <Route
                    path={routes.INFO_SCREEN}
                    element={<InfoContainer />}
                  />
                  <Route
                    path={routes.LOGIN_SCREEN}
                    element={
                      <CustomRedirect
                        redirectTo={
                          isAdminAsset
                            ? routes.ASSET_PROJECTS
                            : routes.V2_DASHBOARD_SCREEN
                        }
                        showChildrenWhen={!props.user}
                      >
                        <LoginScreen />
                      </CustomRedirect>
                    }
                  />
                  <Route
                    path={routes.FORGOT_PASSWORD_SCREEN}
                    element={
                      <CustomRedirect
                        redirectTo={
                          isAdminAsset
                            ? routes.ASSET_PROJECTS
                            : routes.V2_DASHBOARD_SCREEN
                        }
                        showChildrenWhen={!props.user}
                      >
                        <ForgotPasswordScreen />
                      </CustomRedirect>
                    }
                  />
                  <Route
                    path={routes.DASHBOARD_SCREEN + "/*"}
                    element={
                      <CustomRedirect
                        redirectTo={routes.LOGIN_SCREEN}
                        showChildrenWhen={!!props.user}
                      >
                        <DashboardScreen />
                      </CustomRedirect>
                    }
                  />
                  <Route
                    path={routes.V2_DASHBOARD_SCREEN + "/*"}
                    element={
                      <CustomRedirect
                        redirectTo={routes.LOGIN_SCREEN}
                        showChildrenWhen={!!props.user}
                      >
                        <DashboardScreenV2 />
                      </CustomRedirect>
                    }
                  />
                  <Route
                    path={routes.PARTNER_VIEW_ROUTE + "/*"}
                    element={<PartnerView />}
                  />
                  <Route path={"*"} element={<NotFound />} />
                </Routes>
              </ToastProvider>
            </BrowserRouter>
          ) : (
            <div className="tw-h-screen tw-flex tw-items-center tw-justify-center tw-bg-background">
              <span className="tw-text-muted-foreground">Loading...</span>
            </div>
          )}
        </TooltipProvider>
      </FlagProvider>
    </LocalizationProvider>
  );
}

const mapStateToProps = ({
  auth: { user, token },
  stations: { station },
  cities: { city },
}) => ({
  user,
  station,
  city,
  token,
});
const mapDispatchToProps = {
  saveToken,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
